// Shared brief body renderer — used by both the authenticated PursuitBrief
// (/app/pursuits/:id/brief) and the public read-only PublicBrief (/brief/:token).
// Keeps content and section IDs consistent across the two views.
import React from "react";

function fmtCurrency(v) {
  if (v === null || v === undefined || v === "" || v === "—") return "—";
  const s = String(v).trim();
  // Already-formatted (contains any letter or $) → pass through
  if (/[a-zA-Z$]/.test(s)) return s;
  // Numeric-only or comma-separated digits → format as USD
  const n = Number(s.replace(/,/g, ""));
  if (Number.isFinite(n)) return `$${n.toLocaleString("en-US")}`;
  return s;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return "—"; }
}

function deriveRecommendation(raw) {
  const v = (raw || "").trim().toUpperCase();
  if (!v) return "INSUFFICIENT DATA";
  if (v === "GO" || v === "GO WITH CAUTION" || v === "NO GO" || v === "INSUFFICIENT DATA") return v;
  if (v.includes("NO BID") || v.includes("DO NOT PURSUE") || v.includes("NO GO")) return "NO GO";
  if (v.includes("CAUTION")) return "GO WITH CAUTION";
  if (v === "PURSUE" || v.includes("STRONG BID") || v === "BID" || v.startsWith("GO ")) return "GO";
  return "INSUFFICIENT DATA";
}

function recTone(rec) {
  return {
    "GO":               { text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-500" },
    "GO WITH CAUTION":  { text: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-500" },
    "NO GO":            { text: "text-rose-700",    bg: "bg-rose-50",    border: "border-rose-500" },
    "INSUFFICIENT DATA":{ text: "text-slate-700",   bg: "bg-slate-100",  border: "border-slate-400" },
  }[rec] || { text: "text-slate-700", bg: "bg-slate-100", border: "border-slate-400" };
}

function SectionTitle({ n, children }) {
  return (
    <h2 className="font-display font-black text-slate-900 text-lg mb-3 flex items-baseline gap-3 border-b border-slate-300 pb-2">
      <span className="font-mono text-xs tracking-widest text-slate-500">§ {n}</span>
      <span>{children}</span>
    </h2>
  );
}

function BriefField({ label, value, mono, testid }) {
  return (
    <div>
      <dt className="font-mono text-[10px] tracking-widest uppercase text-slate-500">{label}</dt>
      <dd className={`text-slate-900 ${mono ? "font-mono" : ""}`} data-testid={testid}>{value || "—"}</dd>
    </div>
  );
}

function BriefCompliance({ report }) {
  const cr = report.compliance_review || {};
  const far = Array.isArray(report.far_dfars_analysis)
    ? report.far_dfars_analysis
    : (report.far_dfars_analysis?.clauses || []);
  const disq = (cr.disqualification_risks || []).length;
  const highs = far.filter((c) => (c?.risk_level || "").toLowerCase() === "high").length;
  const mods = far.filter((c) => (c?.risk_level || "").toLowerCase() === "moderate").length;
  const cyber = (cr.cybersecurity_requirements || []).length;
  const security = (cr.security_requirements || []).length;
  let status = "Minimal Risk", tone = "emerald";
  if (disq >= 1 || highs >= 2) { status = "High Risk"; tone = "rose"; }
  else if (highs === 1 || mods >= 3 || cyber >= 3 || security >= 3) { status = "Elevated Risk"; tone = "amber"; }
  else if (mods >= 1 || far.length >= 3) { status = "Moderate"; tone = "sky"; }
  const cls = {
    rose:    "text-rose-700 border-rose-500 bg-rose-50",
    amber:   "text-amber-700 border-amber-500 bg-amber-50",
    sky:     "text-sky-700 border-sky-500 bg-sky-50",
    emerald: "text-emerald-700 border-emerald-500 bg-emerald-50",
  }[tone];
  const salient = [
    (cr.cybersecurity_requirements || []).length ? `Cybersecurity: ${cr.cybersecurity_requirements.length} requirement(s)` : null,
    (cr.security_requirements || []).length ? `Security: ${cr.security_requirements.length} requirement(s)` : null,
    (cr.required_certifications || []).length ? `Required certifications: ${cr.required_certifications.length}` : null,
    (cr.subcontracting_requirements || []).length ? `Subcontracting obligations: ${cr.subcontracting_requirements.length}` : null,
    (cr.wage_determinations || []).length ? `Wage determinations: ${cr.wage_determinations.length}` : null,
    disq ? `Disqualification risks: ${disq}` : null,
    far.length ? `FAR/DFARS clauses reviewed: ${far.length}` : null,
  ].filter(Boolean);
  return (
    <div className="space-y-3">
      <div className={`inline-flex items-center gap-2 border-2 px-3 py-1.5 ${cls}`}>
        <span className="font-mono text-[10px] tracking-widest uppercase font-bold" data-testid="cust-brief-compliance-status">{status}</span>
      </div>
      {salient.length > 0 && (
        <ul className="list-disc pl-5 space-y-0.5 text-slate-800 text-sm">
          {salient.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
    </div>
  );
}

// Render the full brief body given the loaded analysis doc.
export function renderBriefBody(doc, { publicMode } = {}) {
  const report = doc.report || {};
  const es = report.executive_summary || {};
  const final = report.final_recommendation || {};
  const intake = doc.intake || {};
  const win = report.win_probability || {};
  const executivePursuit = report.executive_pursuit || {};

  const agency = intake.agency || es.agency || "—";
  const opportunityName = intake.opportunity_name || es.opportunity_name || doc.document_name || "Unnamed pursuit";
  const solicitationNumber = intake.solicitation_number || es.solicitation_number || "—";
  const estimatedValue = fmtCurrency(intake.estimated_ceiling || es.estimated_ceiling || es.estimated_contract_value || es.contract_value);
  const dueDate = intake.response_due_date || es.response_due_date || null;
  const rec = deriveRecommendation(final.verdict);
  const rt = recTone(rec);
  const conf = typeof final.analysis_confidence === "number" ? final.analysis_confidence : null;

  const risks = (report.opportunity_risks || []).slice(0, 5).map((r) => ({
    type: r.type || r.description,
    detail: r.description && r.type ? r.description : (r.mitigation || ""),
    meta: [r.likelihood ? `Likelihood: ${r.likelihood}` : null, r.impact ? `Impact: ${r.impact}` : null].filter(Boolean).join(" · "),
  }));

  const evalC = report.evaluation_criteria || {};
  const keyFactors = (
    (evalC.most_important_factors && evalC.most_important_factors.length ? evalC.most_important_factors : null)
    || evalC.likely_discriminators || evalC.technical_factors || []
  ).slice(0, 6);

  const winThemes = (
    (evalC.likely_discriminators && evalC.likely_discriminators.length ? evalC.likely_discriminators : null)
    || ((report.contractor_match_assessment || {}).strengths || [])
  ).slice(0, 5);

  const nextActions = (final.immediate_next_actions || []).concat(
    (report.top_actions || []).map((a) => (typeof a === "string" ? a : a.action)).filter(Boolean)
  ).slice(0, 8);

  const s6 = winThemes.length > 0 ? 7 : 6;
  const s7 = winThemes.length > 0 ? 8 : 7;

  return (
    <div className="relative z-10 text-slate-900" data-testid="cust-brief-document">
      <header className="border-b-2 border-slate-900 pb-4 mb-6">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">BidNoBidIQ · Executive Opportunity Brief</div>
            <h1 className="font-display font-black text-3xl text-slate-900 mt-1 leading-tight" data-testid="cust-brief-title">{opportunityName}</h1>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Prepared</div>
            <div className="font-mono text-sm text-slate-800">{fmtDate(doc.created_at)}</div>
          </div>
        </div>
      </header>

      <section className="mb-8" data-testid="cust-brief-overview">
        <SectionTitle n={1}>Opportunity Overview</SectionTitle>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <BriefField label="Agency"                testid="cust-brief-agency"       value={agency} />
          <BriefField label="Solicitation Number"   testid="cust-brief-solicitation" value={solicitationNumber} mono />
          <BriefField label="Estimated Contract Value" testid="cust-brief-value"      value={estimatedValue} />
          <BriefField label="Due Date"              testid="cust-brief-due-date"     value={fmtDate(dueDate)} />
          <BriefField label="Contract Type"         value={es.contract_type} />
          <BriefField label="Set-Aside"             value={es.set_aside_status} />
          <BriefField label="Period of Performance" value={es.period_of_performance} />
          <BriefField label="Place of Performance"  value={(report.procurement_profile || {}).place_of_performance} />
        </dl>
        {es.overall_assessment && (
          <p className="text-slate-800 leading-relaxed mt-4 text-[15px]">{es.overall_assessment}</p>
        )}
      </section>

      <section className="mb-8" data-testid="cust-brief-recommendation">
        <SectionTitle n={2}>Go / No-Go Recommendation</SectionTitle>
        <div className={`inline-flex items-center gap-3 border-2 ${rt.border} ${rt.bg} px-4 py-3 mb-3`}>
          <span className={`font-display font-black text-2xl ${rt.text} leading-none`} data-testid="cust-brief-verdict">{rec}</span>
          {conf !== null && (
            <span className="font-mono text-xs text-slate-600 border-l border-slate-400 pl-3">
              <span data-testid="cust-brief-confidence">Analysis Confidence: <strong className={rt.text}>{conf}%</strong></span>
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-500 mb-3" data-testid="cust-brief-confidence-note">
          Analysis Confidence reflects our confidence in the analysis based on the available information.
          It is <strong>not</strong> a probability of contract award.
        </p>
        {final.why && <p className="text-slate-800 leading-relaxed text-[14px]">{final.why}</p>}
        {win.rationale && (
          <div className="mt-3 pt-3 border-t border-slate-200 text-slate-700 text-sm">
            <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">Win Probability · {win.level || "—"} </span>
            <span>{win.rationale}</span>
          </div>
        )}
      </section>

      <section className="mb-8" data-testid="cust-brief-risks">
        <SectionTitle n={3}>Top Pursuit Risks</SectionTitle>
        {risks.length > 0 ? (
          <ol className="space-y-3 text-sm list-decimal pl-5">
            {risks.map((r, i) => (
              <li key={i} className="text-slate-800 leading-relaxed">
                <span className="font-semibold text-slate-900">{r.type}</span>
                {r.detail && <span className="text-slate-700"> — {r.detail}</span>}
                {r.meta && <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mt-0.5">{r.meta}</div>}
              </li>
            ))}
          </ol>
        ) : <p className="text-slate-600 italic text-sm">No material risks surfaced by the analysis.</p>}
      </section>

      <section className="mb-8" data-testid="cust-brief-compliance">
        <SectionTitle n={4}>Compliance Snapshot</SectionTitle>
        <BriefCompliance report={report} />
      </section>

      <section className="mb-8" data-testid="cust-brief-eval-factors">
        <SectionTitle n={5}>Key Evaluation Factors</SectionTitle>
        {keyFactors.length > 0 ? (
          <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
            {keyFactors.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        ) : <p className="text-slate-600 italic text-sm">Not specified in the solicitation.</p>}
      </section>

      {winThemes.length > 0 && (
        <section className="mb-8" data-testid="cust-brief-win-themes">
          <SectionTitle n={6}>Win Themes</SectionTitle>
          <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
            {winThemes.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </section>
      )}

      <section className="mb-8" data-testid="cust-brief-executive-recommendation">
        <SectionTitle n={s6}>Executive Recommendation</SectionTitle>
        {(final.why || executivePursuit.reasoning) && (
          <p className="text-slate-800 leading-relaxed text-[14px] mb-4">{final.why || executivePursuit.reasoning}</p>
        )}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-emerald-700 mb-1">Key Strengths</div>
            {(final.key_strengths || []).length > 0 ? (
              <ul className="list-disc pl-5 space-y-0.5 text-sm text-slate-800">
                {(final.key_strengths || []).map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            ) : <p className="text-slate-500 italic text-sm">—</p>}
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-rose-700 mb-1">Key Concerns</div>
            {(final.key_concerns || []).length > 0 ? (
              <ul className="list-disc pl-5 space-y-0.5 text-sm text-slate-800">
                {(final.key_concerns || []).map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            ) : <p className="text-slate-500 italic text-sm">—</p>}
          </div>
        </div>
      </section>

      <section className="mb-4" data-testid="cust-brief-next-actions">
        <SectionTitle n={s7}>Recommended Next Actions</SectionTitle>
        {nextActions.length > 0 ? (
          <ol className="list-decimal pl-5 space-y-1 text-slate-800 text-sm">
            {nextActions.map((a, i) => <li key={i}>{a}</li>)}
          </ol>
        ) : <p className="text-slate-600 italic text-sm">No specific actions recommended.</p>}
      </section>

      <footer className="border-t border-slate-300 mt-8 pt-4 text-[10px] font-mono tracking-widest uppercase text-slate-500 flex items-center justify-between">
        <span>BidNoBidIQ · Executive Brief · {solicitationNumber}</span>
        <span>{publicMode ? "Prepared by BidNoBidIQ" : `Prepared for ${(doc.contractor_profile || {}).company_name || "—"}`}</span>
      </footer>
    </div>
  );
}

// Also expose the executive summary text for the "Copy Executive Summary" button.
export function executiveSummaryText(doc) {
  const report = doc?.report || {};
  const es = report.executive_summary || {};
  const intake = doc?.intake || {};
  const final = report.final_recommendation || {};
  const rec = deriveRecommendation(final.verdict);
  const conf = typeof final.analysis_confidence === "number" ? final.analysis_confidence : null;
  const opp = intake.opportunity_name || es.opportunity_name || doc?.document_name || "Unnamed pursuit";
  const agency = intake.agency || es.agency || "—";
  const sol = intake.solicitation_number || es.solicitation_number || "—";
  const value = fmtCurrency(intake.estimated_ceiling || es.estimated_ceiling || es.estimated_contract_value || es.contract_value);
  const due = (intake.response_due_date || es.response_due_date) ? new Date(intake.response_due_date || es.response_due_date).toLocaleDateString() : "—";
  const lines = [
    `EXECUTIVE OPPORTUNITY BRIEF — ${opp}`,
    "",
    `Agency: ${agency}`,
    `Solicitation Number: ${sol}`,
    `Estimated Contract Value: ${value}`,
    `Due Date: ${due}`,
    "",
    `Go / No-Go Recommendation: ${rec}${conf !== null ? ` (Analysis Confidence: ${conf}%)` : ""}`,
    "Note: Analysis Confidence reflects confidence in the analysis based on available information — not a probability of contract award.",
    "",
  ];
  if (es.overall_assessment) lines.push("Overview:", es.overall_assessment, "");
  if (final.why) lines.push("Executive Recommendation:", final.why, "");
  if ((final.key_strengths || []).length) {
    lines.push("Key Strengths:", ...final.key_strengths.map((s) => `  • ${s}`), "");
  }
  if ((final.key_concerns || []).length) {
    lines.push("Key Concerns:", ...final.key_concerns.map((s) => `  • ${s}`), "");
  }
  const actions = (final.immediate_next_actions || []).concat(
    (report.top_actions || []).map((a) => (typeof a === "string" ? a : a.action)).filter(Boolean),
  );
  if (actions.length) lines.push("Recommended Next Actions:", ...actions.slice(0, 8).map((s, i) => `  ${i + 1}. ${s}`));
  lines.push("", "— Prepared by BidNoBidIQ");
  return lines.join("\n");
}
